/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Shared RN + NativeWind Activity feed — rebuilt 1:1 to the Figma redesign
// (Ping Desktop `2804:108220` default · `2804:108291` row-hover ·
// `2804:108327` context menu + remind submenu · `2804:108423` selection mode ·
// `2804:108409` custom-reminder modal · `2804:108324` detail-pane placeholder).
// Rendered by both apps/web (in-panel TabView swap) and apps/desktop (the
// master-detail route shell). The view-model, strings, filters and the enriched
// `ActivityItemRecord` shape are re-used from `activity/legacy/`; only the
// presentation is the new NativeWind feed.
//
// Anatomy (Figma `Activity Item/ desktop`): a 32×32 leading visual (reaction
// emoji · stroked icon chip · avatar) + a body (16×16 actor avatar + actor name,
// an action subtitle with a bold tail, then the message body with inline
// @mention pills) + a trailing column. The trailing is, by state:
//   · resting   → a timestamp (or a "Play your turn ▸" / "View Result ▸" CTA),
//   · hovered   → three round icon buttons (mark un/read · erase · ⋮ menu),
//   · selecting → a 20×20 checkbox.
// Right-click / long-press / the ⋮ button opens the context menu (Go to message ·
// Select · Remind me about this ▸). Selection mode swaps the action row for a
// Cancel / Delete footer and the header trailing for a single ✕.
//
// Per the repo rule, every colour/spacing decision is a className token; exact
// Figma values without a token use arbitrary-value classes — never inline style.
// Icons are `lucide-react-native` (cross-platform via react-native-svg) so they
// match the Figma stroked glyphs on web + macOS; their stroke colour is a prop
// (an SVG leaf can't take a className), resolved from the active colour scheme.

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FlatList,
  type ListRenderItem,
  Pressable,
  Text,
  View,
} from "react-native";
import {
  ArrowRight,
  AtSign,
  Bell,
  Calendar,
  Car,
  ChartColumnBig,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Clock3,
  Coins,
  CornerUpLeft,
  Crown,
  Dices,
  EllipsisVertical,
  Hand,
  Heart,
  Info,
  Mail,
  MailOpen,
  MessageSquareText,
  PhoneMissed,
  Plus,
  Reply,
  Spade,
  SquareCheck,
  Trash2,
  X,
} from "lucide-react-native";
import {
  type ActivityCta,
  type ActivityIconName,
  type ActivityItemRecord,
  type ActivityLead,
  type ActivityRowKind,
  type ActivitySort,
  type ActivityTab,
  activityStrings,
  useActivityFeedViewModel,
} from "./legacy";
import type { GameKind } from "../appEvent/types";
import { Avatar } from "../components/Avatar";
import { AnchoredPopover } from "../components/AnchoredPopover";
import { Modal } from "../components/Modal";
import { MrkdwnText } from "../chat/MrkdwnText";
import { useColorScheme } from "../foundations/colorScheme";
import { BellEmptyStateIllustration } from "./BellEmptyStateIllustration";

const SORT_OPTIONS: readonly ActivitySort[] = [
  "mostRecent",
  "byPerson",
  "byChannel",
];

// Brand accent (`accent.brand` === Figma `#537EFF`) — CTA labels + the
// custom-reminder primary button + mention-pill foreground.
const BRAND = "#537EFF";

// ─────────────────────────────────────────────────────────────────────────────
// Scheme-resolved icon colours. Icons are SVG leaves so their stroke is a prop,
// not a className; we resolve a small palette from the active colour scheme.

interface IconPalette {
  /** Default glyph colour (menu rows · hover buttons · context icons). */
  readonly icon: string;
  /** Muted glyph colour (action-row buttons · dropdown placeholders). */
  readonly muted: string;
  /** Date-divider / placeholder text colour. */
  readonly faint: string;
}

function useIconPalette(): IconPalette {
  const dark = useColorScheme() === "dark";
  return dark
    ? { icon: "#E8EAED", muted: "#8C929E", faint: "#6B7280" }
    : { icon: "#374151", muted: "#6B7280", faint: "#9CA3AF" };
}

// ─────────────────────────────────────────────────────────────────────────────

/** Host actions wired by `ActivityRouteHost`. All optional so the screen renders
 *  standalone (e.g. in tests / storybook) with no host. */
export interface ActivityFeedActions {
  /** Open the conversation at the activity's anchor message. */
  readonly onGoToMessage?: (item: ActivityItemRecord) => void;
  readonly onMarkRead?: (item: ActivityItemRecord) => void;
  readonly onMarkUnread?: (item: ActivityItemRecord) => void;
  readonly onMarkAllRead?: () => void;
  readonly onErase?: (item: ActivityItemRecord) => void;
  /** Schedule a reminder for `item` to fire at `fireAt` (wall-clock ms). */
  readonly onRemind?: (item: ActivityItemRecord, fireAt: number) => void;
  readonly onClearReminder?: (item: ActivityItemRecord) => void;
  /** Press the inline CTA (game rows). */
  readonly onCtaPress?: (item: ActivityItemRecord, cta: ActivityCta) => void;
}

export interface ActivityFeedScreenProps extends ActivityFeedActions {
  readonly load: import("./legacy").ActivityLoader;
  readonly subscribe: import("./legacy").ActivitySubscriber;
  readonly onItemPress?: (item: ActivityItemRecord) => void;
  /** When provided, a ✕ in the header swaps the feed back to the conversation
   *  list (web in-panel TabView). Also the Figma `Side pannel header` close. */
  readonly onClose?: () => void;
  /** Optional compose affordance (Figma header `+`). Inert when omitted. */
  readonly onCompose?: () => void;
}

export const ActivityFeedScreen: React.FC<ActivityFeedScreenProps> = ({
  load,
  subscribe,
  onItemPress,
  onClose,
  onCompose,
  ...actions
}) => {
  const vm = useActivityFeedViewModel({ load, subscribe });
  const [sortVisible, setSortVisible] = useState(false);
  const sortBtnRef = useRef<View>(null);
  const [tab, setTab] = useState<ActivityTab>("all");

  // Selection mode (Figma `2804:108423`): a multi-select layer over the feed.
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const allItems = vm.state.kind === "populated" ? vm.state.items : EMPTY_ITEMS;
  const unreadCount = useMemo(
    () => allItems.reduce((n, it) => n + (it.unread ? 1 : 0), 0),
    [allItems],
  );
  const visibleItems = useMemo(
    () => (tab === "unread" ? allItems.filter((it) => it.unread) : allItems),
    [allItems, tab],
  );
  const sections = useMemo(() => groupByDay(visibleItems), [visibleItems]);

  const enterSelection = useCallback((seedId?: string) => {
    setSelecting(true);
    setSelectedIds(seedId != null ? new Set([seedId]) : new Set());
  }, []);
  const exitSelection = useCallback(() => {
    setSelecting(false);
    setSelectedIds(new Set());
  }, []);
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const deleteSelected = useCallback(() => {
    for (const it of allItems) {
      if (selectedIds.has(it.id)) actions.onErase?.(it);
    }
    exitSelection();
    // actions is a stable host ref bag for a render pass
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, selectedIds, exitSelection]);

  const renderBody = (): React.ReactElement => {
    if (vm.state.kind === "loading") {
      return <ActivitySkeleton />;
    }
    if (vm.state.kind === "error") {
      return (
        <View className="flex-1 items-center justify-center p-xl">
          <Text className="text-headline font-semibold text-light-text-primary dark:text-[#F5F6F8] mb-md text-center">
            {activityStrings.errorTitle}
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={activityStrings.errorRetry}
            onPress={vm.retry}
            className="px-lg py-sm rounded-md bg-[#537EFF] active:opacity-80"
          >
            <Text className="text-footnote font-semibold text-white">
              {activityStrings.errorRetry}
            </Text>
          </Pressable>
        </View>
      );
    }
    if (visibleItems.length === 0) {
      return <ActivityEmptyState />;
    }
    return (
      <FlatList<ActivitySectionRow>
        data={sections}
        renderItem={renderSectionRow}
        keyExtractor={sectionRowKey}
        windowSize={10}
        initialNumToRender={20}
        maxToRenderPerBatch={12}
        removeClippedSubviews={false}
        contentContainerClassName="pb-xl"
      />
    );
  };

  const renderSectionRow: ListRenderItem<ActivitySectionRow> = useCallback(
    ({ item }) =>
      item.type === "header" ? (
        <ActivityDateDivider label={item.label} />
      ) : (
        <ActivityRow
          item={item.item}
          onPress={onItemPress}
          actions={actions}
          selecting={selecting}
          selected={selectedIds.has(item.item.id)}
          onToggleSelect={toggleSelected}
          onEnterSelection={enterSelection}
        />
      ),
    // actions/onItemPress are stable host refs for a render pass
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onItemPress, selecting, selectedIds, toggleSelected, enterSelection],
  );

  return (
    <View
      accessibilityLabel={activityStrings.screenTitle}
      className="flex-1 flex flex-col gap-sm border-r border-light-border-subtle dark:border-dark-border-subtle"
    >
      {/* Header — title + trailing buttons (Figma `Side pannel header`). In
			    selection mode the trailing collapses to a single ✕ (exit selection). */}

      <View
        className="relative z-50 flex flex-row items-center justify-between px-md h-12 bg-light-surface-panel dark:bg-dark-surface-panel"
        style={{ zIndex: 50 }}
      >
        <Text className="font-sans font-bold text-xl text-light-text-primary dark:text-[#F5F6F8]">
          {activityStrings.screenTitle}
        </Text>
        <View className="flex flex-row items-center" style={{ columnGap: 4 }}>
          <View className="relative">
            {selecting ? (
              <HeaderIconButton
                label={activityStrings.customReminderCancel}
                icon={X}
                onPress={exitSelection}
              />
            ) : (
              // Figma `2804:108220` header: notification bell + compose. The bell
              // doubles as the dismiss/toggle-back when the feed is an in-panel
              // scene (`onClose`); compose is wired when the host supplies it.
              <View
                className="flex flex-row items-center"
                style={{ columnGap: 4 }}
              >
                <HeaderIconButton
                  label={
                    onClose != null
                      ? activityStrings.close
                      : activityStrings.screenTitle
                  }
                  icon={Bell}
                  {...(onClose != null ? { onPress: onClose } : {})}
                />
                <HeaderIconButton
                  label="New"
                  icon={Plus}
                  {...(onCompose != null ? { onPress: onCompose } : {})}
                />
              </View>
            )}
          </View>
        </View>
      </View>

      <ActivityTabs
        tab={tab}
        onChange={setTab}
        allCount={allItems.length}
        unreadCount={unreadCount}
      />

      {/* Action row — "Mark all as read" + "Sort" bordered text+icon buttons
			    (Figma `Frame 1000004950`). Hidden while selecting. */}
      {!selecting && (
        <View className="flex flex-row justify-between items-center gap-sm px-md">
          <ActionButton
            label={activityStrings.markAllRead}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="14"
                viewBox="0 0 15 14"
                fill="none"
              >
                <path
                  d="M4.75 6.08333L6.75 8.08333L13.4167 1.41667M9.41667 0.75H3.95C2.82989 0.75 2.26984 0.75 1.84202 0.967987C1.46569 1.15973 1.15973 1.46569 0.967987 1.84202C0.75 2.26984 0.75 2.82989 0.75 3.95V9.55C0.75 10.6701 0.75 11.2301 0.967987 11.658C1.15973 12.0343 1.46569 12.3403 1.84202 12.532C2.26984 12.75 2.82989 12.75 3.95 12.75H9.55C10.6701 12.75 11.2301 12.75 11.658 12.532C12.0343 12.3403 12.3403 12.0343 12.532 11.658C12.75 11.2301 12.75 10.6701 12.75 9.55V6.75"
                  stroke="#8C929E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            onPress={actions.onMarkAllRead}
            disabled={unreadCount === 0}
          />
          <View ref={sortBtnRef}>
            <ActionButton
              label={activityStrings.sortLabel}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="10"
                  viewBox="0 0 14 10"
                  fill="none"
                >
                  <path
                    d="M2.75 4.75H10.75M0.75 0.75H12.75M4.75 8.75H8.75"
                    stroke="#8C929E"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              onPress={() => setSortVisible((v) => !v)}
            />
          </View>
        </View>
      )}

      {renderBody()}

      {/* Selection footer (Figma `2804:108423`): Cancel + destructive Delete. */}
      {selecting && (
        <View className="flex flex-row items-center justify-end gap-sm px-md pb-sm">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={activityStrings.customReminderCancel}
            onPress={exitSelection}
            className="flex flex-row items-center h-9 px-3 rounded-lg active:opacity-70"
          >
            <Text className="text-sm text-light-text-primary dark:text-[#F5F6F8]">
              {activityStrings.customReminderCancel}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={activityStrings.menuErase}
            accessibilityState={{ disabled: selectedIds.size === 0 }}
            disabled={selectedIds.size === 0}
            onPress={deleteSelected}
            className={`flex flex-row items-center h-9 px-3 rounded-lg bg-[#FF5353] active:opacity-80 ${
              selectedIds.size === 0 ? "opacity-40" : ""
            }`}
          >
            <Text className="text-sm font-bold text-white">
              {activityStrings.deleteSelected}
            </Text>
          </Pressable>
        </View>
      )}

      <ActivitySortPopover
        anchorRef={sortBtnRef}
        open={sortVisible}
        active={vm.sort}
        onSelect={(s: ActivitySort) => {
          vm.setSort(s);
          setSortVisible(false);
        }}
        onClose={() => setSortVisible(false)}
      />
    </View>
  );
};

const EMPTY_ITEMS: readonly ActivityItemRecord[] = Object.freeze([]);

// A hovered row keeps its trailing action cluster (mark-read · erase · ⋮)
// mounted for a short beat after the pointer leaves. The cluster mounting
// reflows the row (the flex-1 body reclaims the width the timestamp gave up),
// and reaching for the top-right ⋮ can graze the row's edge — either can fire a
// transient RN-web `pointerleave`. Debouncing the collapse lets the immediate
// re-enter cancel it, so the buttons don't vanish out from under the cursor.
const HOVER_OUT_GRACE_MS = 150;

type LucideIcon = typeof Bell;

// ── Header icon button (28×28, r8, `#2F333B` border — Figma `Button`) ────────

function HeaderIconButton({
  label,
  icon: Icon,
  onPress,
}: {
  label: string;
  icon: LucideIcon;
  onPress?: (() => void) | undefined;
}): JSX.Element {
  const pal = useIconPalette();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      disabled={!onPress}
      className="flex items-center justify-center w-7 h-7 rounded-[6px] hover:bg-light-surface-elevated dark:hover:bg-dark-surface-elevated"
      style={{ opacity: !onPress ? 0.5 : 1, pointerEvents: !onPress ? 'none' : 'auto' }}
    >
      <Icon size={18} color={pal.icon} strokeWidth={1.75} />
    </Pressable>
  );
}

/** Bordered text+icon button (Figma `Frame 1000004950`): 28px tall, r8,
 *  `#2F333B` border, 16px leading glyph + 14/700 `#B5BAC3` label. `icon` is a
 *  rendered node (an inline `<svg>` or any element) — not a component — so the
 *  caller controls its exact glyph/size/stroke. */
function ActionButton({
  label,
  icon,
  onPress,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  onPress?: (() => void) | undefined;
  disabled?: boolean | undefined;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: disabled ?? false }}
      disabled={disabled ?? false}
      onPress={onPress}
      className={`flex flex-row items-center gap-sm h-7 px-2 rounded-sm border border-light-border-secondary dark:border-[#2F333B] active:opacity-70 ${
        disabled ? "opacity-40" : ""
      }`}
    >
      {icon}
      <Text className="text-xs font-medium text-light-text-secondary dark:text-[#B5BAC3]">
        {label}
      </Text>
    </Pressable>
  );
}

// ── Tabs: All | Unread, with dark count pills (Figma `Horizontal tabs`) ───────

function ActivityTabs({
  tab,
  onChange,
  allCount,
  unreadCount,
}: {
  tab: ActivityTab;
  onChange: (t: ActivityTab) => void;
  allCount: number;
  unreadCount: number;
}): JSX.Element {
  return (
    <View className="flex flex-row gap-[2px] px-md">
      <ActivityTabButton
        label={activityStrings.tabAll}
        count={allCount}
        selected={tab === "all"}
        onPress={() => onChange("all")}
      />
      <ActivityTabButton
        label={activityStrings.tabUnread}
        count={unreadCount}
        selected={tab === "unread"}
        onPress={() => onChange("unread")}
      />
    </View>
  );
}

function ActivityTabButton({
  label,
  count,
  selected,
  onPress,
}: {
  label: string;
  count: number;
  selected: boolean;
  onPress: () => void;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityLabel={`${label}, ${count}`}
      onPress={onPress}
      className={`flex-1 flex-row items-center justify-center gap-sm h-[30px] rounded-xl active:opacity-80 ${
        selected
          ? "bg-light-surface-secondary dark:bg-[#222429]"
          : "bg-transparent"
      }`}
    >
      <Text
        className={`text-sm font-medium ${
          selected
            ? "text-light-text-primary dark:text-white"
            : "text-[#717680]"
        }`}
      >
        {label}
      </Text>
      {count > 0 && !selected && (
        // The selected tab hides its count (its list is on-screen); the
        // unselected tab shows how many it holds — matching Figma 108220
        // (All selected → no badge, Unread → "1").
        <View className="min-w-[20px] h-5 px-2 rounded-full items-center justify-center bg-light-surface-secondary dark:bg-[#222429]">
          <Text className="text-sm font-bold text-light-text-primary dark:text-white">
            {String(count)}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

// ── Date-section divider (Figma `Frame 1000004951`) ──────────────────────────

function ActivityDateDivider({ label }: { label: string }): JSX.Element {
  return (
    <View className="flex flex-row items-center gap-sm pt-sm pb-xs px-md">
      <Text className="text-xs text-[#6B7280]">{label}</Text>
      <View className="flex-1 h-px bg-light-border-secondary dark:bg-[#2F333B]" />
    </View>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────────

interface ActivityRowProps {
  readonly item: ActivityItemRecord;
  readonly onPress?: ((item: ActivityItemRecord) => void) | undefined;
  readonly actions: ActivityFeedActions;
  readonly selecting: boolean;
  readonly selected: boolean;
  readonly onToggleSelect: (id: string) => void;
  readonly onEnterSelection: (seedId?: string) => void;
}

function ActivityRow({
  item,
  onPress,
  actions,
  selecting,
  selected,
  onToggleSelect,
  onEnterSelection,
}: ActivityRowProps): JSX.Element {
  const anchorRef = useRef<View>(null);
  // The ⋮ button is the context menu's anchor so the popover drops directly
  // beneath it. It's only mounted while the row is active, so fall back to the
  // whole row (e.g. a native long-press opens the menu before the ⋮ mounts).
  const moreBtnRef = useRef<View>(null);
  const menuAnchorRef = useMemo<React.RefObject<View>>(
    () => ({
      get current(): View | null {
        return moreBtnRef.current ?? anchorRef.current;
      },
    }),
    [],
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [submenu, setSubmenu] = useState<"root" | "remind">("root");
  const [customOpen, setCustomOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const formattedTime = formatRelative(item.timestamp);

  // Debounced hover-out (see HOVER_OUT_GRACE_MS): a hover-in cancels any pending
  // collapse and reveals immediately; a hover-out schedules the collapse so a
  // momentary boundary flicker while reaching for the ⋮ doesn't tear the cluster
  // down. The timer is cleared on unmount.
  const hoverOutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHoverTimer = useCallback(() => {
    if (hoverOutTimer.current !== null) {
      clearTimeout(hoverOutTimer.current);
      hoverOutTimer.current = null;
    }
  }, []);
  useEffect(() => clearHoverTimer, [clearHoverTimer]);
  const handleHoverIn = useCallback(() => {
    clearHoverTimer();
    setHovered(true);
  }, [clearHoverTimer]);
  const handleHoverOut = useCallback(() => {
    clearHoverTimer();
    hoverOutTimer.current = setTimeout(() => {
      hoverOutTimer.current = null;
      setHovered(false);
    }, HOVER_OUT_GRACE_MS);
  }, [clearHoverTimer]);

  const openMenu = useCallback(() => {
    clearHoverTimer();
    setHovered(true);
    setSubmenu("root");
    setMenuOpen(true);
  }, [clearHoverTimer]);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Hover is owned by the WRAPPING View via raw DOM mouse/pointer events, NOT
  // the row Pressable's `onHoverIn/onHoverOut`. RN-Web `Pressable` uses
  // *contained* hover: entering a nested action button (itself a Pressable)
  // dispatches a hover-lock that bubbles up and ENDS the row's hover — so the
  // cluster collapsed the instant the cursor reached the ⋮. The wrapper's plain
  // DOM events have no containment, and the buttons live inside it, so hovering
  // them keeps the row hovered. (Same fix as `ConversationListPanel`.) Guarded
  // to mouse input so a touch on native never reveals the hover cluster.
  const rowHoverHandlers = useMemo(() => {
    const isMouse = (e: { nativeEvent?: { pointerType?: string } }): boolean =>
      e?.nativeEvent?.pointerType === undefined ||
      e.nativeEvent.pointerType === "mouse";
    return {
      onMouseEnter: handleHoverIn,
      onMouseLeave: handleHoverOut,
      onPointerEnter: (e: { nativeEvent?: { pointerType?: string } }) => {
        if (isMouse(e)) handleHoverIn();
      },
      onPointerLeave: (e: { nativeEvent?: { pointerType?: string } }) => {
        if (isMouse(e)) handleHoverOut();
      },
    } as Record<string, unknown>;
  }, [handleHoverIn, handleHoverOut]);

  // The row is "active" (its trailing action cluster revealed) while the cursor
  // is over it OR while its context menu is open. Keeping the cluster mounted
  // while the menu is open is what lets the cursor leave the row and travel onto
  // the (body-portaled) menu without the row collapsing back to its timestamp —
  // otherwise `hovered` flips false the instant the pointer leaves the row and
  // the whole affordance (⋮ included) vanishes, reading as "the menu closed".
  const active = hovered || menuOpen;

  const handlePress = useCallback(() => {
    if (selecting) {
      onToggleSelect(item.id);
      return;
    }
    // Opening an activity marks it read (synced across devices).
    if (item.unread) actions.onMarkRead?.(item);
    onPress?.(item);
    // stable refs for a render pass
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecting, item, onPress, onToggleSelect]);

  return (
    <View ref={anchorRef} className="px-md" {...rowHoverHandlers}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={selecting ? { selected, checked: selected } : {}}
        accessibilityLabel={`${activityStrings.rowKindLabel(item.kind)}, ${
          item.actorName ?? ""
        }, ${formattedTime}`}
        onPress={handlePress}
        onLongPress={() => (selecting ? onToggleSelect(item.id) : openMenu())}
        className="flex flex-row items-start gap-md pt-md pb-md rounded-lg active:bg-light-surface-secondary dark:active:bg-[#222429]"
      >
        <ActivityLeadVisual
          lead={item.lead ?? leadForKind(item.kind)}
          fallbackInitials={item.actorName ?? undefined}
        />

        <View className="flex-1 flex flex-col gap-1">
          {/* Actor avatar + name (Figma `Frame 1000004306`) */}
          <View className="flex flex-row items-center gap-1">
            <Avatar
              size="company"
              shape="rounded"
              {...(item.actorAvatarUri != null
                ? { imageUri: item.actorAvatarUri }
                : {})}
              initials={item.actorName ?? "·"}
              accessibilityLabel={item.actorName ?? ""}
            />
            <Text
              numberOfLines={1}
              className="text-sm leading-[22px] font-bold text-light-text-primary dark:text-white"
            >
              {item.actorName ?? activityStrings.rowKindLabel(item.kind)}
            </Text>
          </View>

          {/* Action subtitle (e.g. "Reacted to your message." / "Thread in
           **Dev Group chat**") — 12/400 with a bold tail. */}
          {(item.actionLabel ?? "").length > 0 && (
            <Text
              numberOfLines={1}
              className="text-xs leading-[16px] text-light-text-secondary dark:text-[#B5BAC3]"
            >
              {item.actionLabel}
              {item.actionEmphasis != null && (
                <Text className="text-xs leading-[16px] font-bold text-light-text-secondary dark:text-[#B5BAC3]">
                  {` ${item.actionEmphasis}`}
                </Text>
              )}
            </Text>
          )}

          {/* Message body — 14/20 with inline @mention pills (Figma
					    `Frame 1000004311`/`Frame 1000004310`). */}
          <ActivityBody item={item} />
        </View>

        {/* Trailing: selecting → checkbox · hovered → action buttons ·
				    CTA row → CTA · else → timestamp. */}
        {selecting ? (
          <SelectionCheckbox selected={selected} />
        ) : active ? (
          <RowHoverActions
            item={item}
            actions={actions}
            onMore={openMenu}
            moreRef={moreBtnRef}
          />
        ) : item.cta != null ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={item.cta.label}
            onPress={(e) => {
              e?.stopPropagation?.();
              actions.onCtaPress?.(item, item.cta as ActivityCta);
            }}
            className="flex-row items-center gap-sm h-[38px] active:opacity-80"
          >
            <Text className="text-sm font-bold text-[#537EFF]">
              {item.cta.label}
            </Text>
            <ArrowRight size={20} color={BRAND} strokeWidth={1.5} />
          </Pressable>
        ) : (
          <View className="flex flex-col items-end pt-[2px]">
            <Text className="text-sm leading-5 shrink-0 text-light-text-tertiary dark:text-white">
              {formattedTime}
            </Text>
          </View>
        )}
      </Pressable>

      {/* Full-width hairline under the content (Figma `Line 283`). */}
      <View className="h-px bg-light-border-secondary dark:bg-[#222429]" />

      <ActivityContextMenu
        anchorRef={menuAnchorRef}
        open={menuOpen}
        submenu={submenu}
        item={item}
        actions={actions}
        onClose={closeMenu}
        onSelect={() => {
          closeMenu();
          onEnterSelection(item.id);
        }}
        onOpenRemind={() => setSubmenu("remind")}
        onBackToRoot={() => setSubmenu("root")}
        onOpenCustom={() => {
          setMenuOpen(false);
          setCustomOpen(true);
        }}
      />

      <CustomReminderSheet
        visible={customOpen}
        onClose={() => setCustomOpen(false)}
        onCreate={(fireAt) => {
          setCustomOpen(false);
          actions.onRemind?.(item, fireAt);
        }}
      />
    </View>
  );
}

/** Message body — `MrkdwnText` when a rich body is present (renders @mention
 *  pills: bg `#2443A3`, fg `#537EFF`), else the plain preview string. */
function ActivityBody({
  item,
}: {
  item: ActivityItemRecord;
}): JSX.Element | null {
  const scheme = useColorScheme();
  const body =
    (item.bodyText ?? "").length > 0 ? item.bodyText! : item.previewText;
  if (body.length === 0) return null;
  const textColor = scheme === "dark" ? "#F5F6F8" : "#1F2430";
  return (
    <MrkdwnText
      body={body}
      mentions={item.bodyMentions}
      textStyle={{ fontSize: 14, lineHeight: 20, color: textColor }}
      palette={{
        link: BRAND,
        mention: BRAND,
        mentionBackground: "#2443A3",
        code: scheme === "dark" ? "#B5BAC3" : "#6B7280",
        codeBackground: scheme === "dark" ? "#222429" : "#F3F4F6",
        quoteBar: scheme === "dark" ? "#2F333B" : "#D1D5DB",
      }}
    />
  );
}

// ── Selection checkbox (Figma `Checkbox`: 20×20, r6, `#537EFF` when checked) ──

function SelectionCheckbox({ selected }: { selected: boolean }): JSX.Element {
  return (
    <View className="pt-[2px]">
      <View
        className={`w-5 h-5 rounded-md items-center justify-center ${
          selected
            ? "bg-[#537EFF]"
            : "border border-light-border-secondary dark:border-[#2F333B]"
        }`}
      >
        {selected && <Check size={14} color="#FFFFFF" strokeWidth={2} />}
      </View>
    </View>
  );
}

// ── Row hover actions (Figma `Frame 1000004952`: 3 round 32×32 buttons) ──────

function RowHoverActions({
  item,
  actions,
  onMore,
  moreRef,
}: {
  item: ActivityItemRecord;
  actions: ActivityFeedActions;
  onMore: () => void;
  /** Ref on the ⋮ button so the context menu anchors directly beneath it. */
  moreRef?: React.RefObject<View>;
}): JSX.Element {
  const pal = useIconPalette();
  const canToggleRead = item.unread
    ? actions.onMarkRead != null
    : actions.onMarkUnread != null;
  return (
    <View className="flex flex-row items-center gap-[2px]">
      {canToggleRead && (
        <RoundIconButton
          label={
            item.unread
              ? activityStrings.menuMarkRead
              : activityStrings.menuMarkUnread
          }
          icon={item.unread ? MailOpen : Mail}
          color={pal.icon}
          onPress={() =>
            item.unread
              ? actions.onMarkRead?.(item)
              : actions.onMarkUnread?.(item)
          }
        />
      )}
      {actions.onErase != null && (
        <RoundIconButton
          label={activityStrings.menuErase}
          icon={Trash2}
          color={pal.icon}
          onPress={() => actions.onErase?.(item)}
        />
      )}
      <RoundIconButton
        label={activityStrings.menuMore}
        icon={EllipsisVertical}
        color={pal.icon}
        onPress={onMore}
        {...(moreRef != null ? { buttonRef: moreRef } : {})}
      />
    </View>
  );
}

function RoundIconButton({
  label,
  icon: Icon,
  color,
  onPress,
  buttonRef,
}: {
  label: string;
  icon: LucideIcon;
  color: string;
  onPress: () => void;
  /** Anchor ref (e.g. so the ⋮ button anchors its context-menu popover). */
  buttonRef?: React.RefObject<View>;
}): JSX.Element {
  return (
    <Pressable
      ref={buttonRef}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={(e) => {
        e?.stopPropagation?.();
        onPress();
      }}
      className="w-8 h-8 rounded-full items-center justify-center hover:bg-light-surface-elevated dark:hover:bg-[#2F333B] active:opacity-70"
    >
      <Icon size={16} color={color} strokeWidth={1.5} />
    </Pressable>
  );
}

// ── Leading visual ───────────────────────────────────────────────────────────

function ActivityLeadVisual({
  lead,
  fallbackInitials,
}: {
  lead: ActivityLead;
  /** Actor initials to show when an `avatar` lead has no image (e.g. a game
   *  challenger with no picture) — avoids a blank chip. */
  fallbackInitials?: string | undefined;
}): JSX.Element {
  const pal = useIconPalette();
  if (lead.type === "emoji") {
    return (
      <View className="w-8 h-8 items-center justify-center">
        <Text className="text-[24px] leading-[34px]">{lead.value}</Text>
      </View>
    );
  }
  if (lead.type === "avatar") {
    return (
      <Avatar
        size="small"
        shape="rounded"
        {...(lead.uri != null ? { imageUri: lead.uri } : {})}
        {...(fallbackInitials != null ? { initials: fallbackInitials } : {})}
        accessibilityLabel={fallbackInitials ?? ""}
      />
    );
  }
  // A game row's leading visual is that game's own icon in a stroked chip.
  if (lead.type === "gameIcon") {
    const GameIcon = GAME_LEAD_ICON[lead.kind];
    return (
      <View className="w-8 h-8 rounded-[8px] items-center justify-center bg-light-surface-secondary dark:bg-[#222429]">
        <GameIcon size={20} color={pal.icon} strokeWidth={1.75} />
      </View>
    );
  }
  const Icon = leadIconComponent(lead.name);
  return (
    <View className="w-8 h-8 rounded-[8px] items-center justify-center bg-light-surface-secondary dark:bg-[#222429]">
      <Icon size={20} color={pal.icon} strokeWidth={1.5} />
    </View>
  );
}

/** Per-game lead icon — mirrors the game picker's glyphs (Figma game tiles):
 *  coin flip · dice · rock-paper-scissors · chess · blackjack · racing. */
const GAME_LEAD_ICON: Record<GameKind, LucideIcon> = {
  coinflip: Coins,
  dice: Dices,
  rps: Hand,
  chess: Crown,
  blackjack: Spade,
  racing: Car,
};

function leadIconComponent(name: ActivityIconName): LucideIcon {
  switch (name) {
    case "mention":
      return AtSign;
    case "thread":
      return Reply;
    case "reaction":
      return Heart;
    case "reminder":
      return Clock3;
    case "missedCall":
      return PhoneMissed;
    case "poll":
      return ChartColumnBig;
    case "meeting":
      return Calendar;
    case "system":
      return Info;
  }
}

function leadForKind(kind: ActivityRowKind): ActivityLead {
  switch (kind) {
    case "mention":
      return { type: "icon", name: "mention" };
    case "reply":
    case "dm":
      return { type: "icon", name: "thread" };
    case "reaction":
      return { type: "icon", name: "reaction" };
    case "reminder":
      return { type: "icon", name: "reminder" };
    case "missedCall":
      return { type: "icon", name: "missedCall" };
    case "poll":
      return { type: "icon", name: "poll" };
    case "meeting":
      return { type: "icon", name: "meeting" };
    case "game":
      return { type: "avatar" };
    case "task":
    case "system":
      return { type: "icon", name: "system" };
  }
}

// ── Context menu + remind submenu (Figma `Option menu` 2804:108327) ──────────

function ActivityContextMenu({
  anchorRef,
  open,
  submenu,
  item,
  actions,
  onClose,
  onSelect,
  onOpenRemind,
  onBackToRoot,
  onOpenCustom,
}: {
  anchorRef: React.RefObject<View>;
  open: boolean;
  submenu: "root" | "remind";
  item: ActivityItemRecord;
  actions: ActivityFeedActions;
  onClose: () => void;
  onSelect: () => void;
  onOpenRemind: () => void;
  onBackToRoot: () => void;
  onOpenCustom: () => void;
}): JSX.Element | null {
  if (!open) return null;

  const remind = (deltaMs: number): void => {
    onClose();
    actions.onRemind?.(item, Date.now() + deltaMs);
  };

  return (
    <AnchoredPopover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      placement="bottom"
      align="end"
    >
      <View className="min-w-[220px] rounded-2xl p-1 bg-white dark:bg-[#15171C] border border-light-border-secondary dark:border-[#2F333B]">
        {submenu === "root" ? (
          <>
            {actions.onGoToMessage && (
              <MenuItem
                label={activityStrings.menuGoToMessage}
                icon={MessageSquareText}
                onPress={() => {
                  onClose();
                  actions.onGoToMessage?.(item);
                }}
              />
            )}
            <MenuItem
              label={activityStrings.menuSelect}
              icon={SquareCheck}
              onPress={onSelect}
            />
            {actions.onRemind && (
              <MenuItem
                label={activityStrings.menuRemind}
                icon={Clock3}
                trailing={ChevronRight}
                highlighted
                onPress={onOpenRemind}
              />
            )}
          </>
        ) : (
          <>
            <MenuItem
              label={activityStrings.menuRemind}
              icon={CornerUpLeft}
              onPress={onBackToRoot}
            />
            <MenuItem
              label={activityStrings.remind15m}
              onPress={() => remind(15 * 60_000)}
            />
            <MenuItem
              label={activityStrings.remind30m}
              onPress={() => remind(30 * 60_000)}
            />
            <MenuItem
              label={activityStrings.remind1h}
              onPress={() => remind(60 * 60_000)}
            />
            <MenuItem
              label={activityStrings.remindTomorrow}
              onPress={() => remind(nextMorningDeltaMs())}
            />
            <MenuItem
              label={activityStrings.remindNextWeek}
              onPress={() => remind(nextWeekDeltaMs())}
            />
            <MenuItem
              label={activityStrings.remindCustom}
              onPress={onOpenCustom}
            />
          </>
        )}
      </View>
    </AnchoredPopover>
  );
}

function MenuItem({
  label,
  icon: Icon,
  trailing: Trailing,
  highlighted,
  onPress,
}: {
  label: string;
  icon?: LucideIcon;
  trailing?: LucideIcon;
  highlighted?: boolean;
  onPress: () => void;
}): JSX.Element {
  const pal = useIconPalette();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      className={`flex flex-row items-center gap-sm px-2 h-9 rounded-xl hover:bg-light-surface-secondary dark:hover:bg-[#222429] active:bg-light-surface-secondary dark:active:bg-[#222429] ${
        highlighted ? "bg-light-surface-secondary dark:bg-[#222429]" : ""
      }`}
    >
      {Icon != null && <Icon size={20} color={pal.icon} strokeWidth={1.5} />}
      <Text className="flex-1 text-sm font-medium text-light-text-primary dark:text-[#F5F6F8]">
        {label}
      </Text>
      {Trailing != null && (
        <Trailing size={14} color={pal.muted} strokeWidth={1.5} />
      )}
    </Pressable>
  );
}

// ── Custom reminder modal (Figma `2804:108409`) ──────────────────────────────

function CustomReminderSheet({
  visible,
  onClose,
  onCreate,
}: {
  visible: boolean;
  onClose: () => void;
  onCreate: (fireAt: number) => void;
}): JSX.Element {
  const pal = useIconPalette();
  const [dateOffset, setDateOffset] = useState<number | null>(null);
  const [minuteOfDay, setMinuteOfDay] = useState<number | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const dateRef = useRef<View>(null);
  const timeRef = useRef<View>(null);

  const dateOptions = useMemo(() => buildDateOptions(), [visible]);
  const timeOptions = useMemo(() => buildTimeOptions(), []);

  const ready = dateOffset !== null && minuteOfDay !== null;
  const dateLabel =
    dateOffset !== null
      ? (dateOptions[dateOffset]?.label ?? activityStrings.customReminderDate)
      : activityStrings.customReminderDate;
  const timeLabel =
    minuteOfDay !== null
      ? (timeOptions.find((t) => t.minute === minuteOfDay)?.label ??
        activityStrings.customReminderTime)
      : activityStrings.customReminderTime;

  const reset = (): void => {
    setDateOffset(null);
    setMinuteOfDay(null);
    setDateOpen(false);
    setTimeOpen(false);
  };
  const close = (): void => {
    reset();
    onClose();
  };
  const create = (): void => {
    if (dateOffset === null || minuteOfDay === null) return;
    const base = dateOptions[dateOffset]?.dayStart;
    if (base === undefined) return;
    reset();
    onCreate(base + minuteOfDay * 60_000);
  };

  return (
    <Modal
      visible={visible}
      onClose={close}
      accessibilityLabel={activityStrings.customReminderTitle}
    >
      <View className="w-[521px] max-w-full gap-md">
        {/* Title row */}
        <View className="flex flex-row items-center px-1">
          <Text className="flex-1 text-[18px] leading-[25px] font-bold text-light-text-primary dark:text-white">
            {activityStrings.customReminderTitle}
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={activityStrings.close}
            onPress={close}
            className="w-7 h-7 rounded-xl items-center justify-center hover:bg-light-surface-elevated dark:hover:bg-[#222429] active:opacity-70"
          >
            <X size={20} color={pal.icon} strokeWidth={1.5} />
          </Pressable>
        </View>

        {/* Two dropdown fields */}
        <View className="flex flex-row gap-md">
          <View ref={dateRef} className="flex-1 gap-1">
            <Text className="text-xs text-light-text-primary dark:text-[#F5F6F8]">
              {activityStrings.customReminderSelect}
            </Text>
            <DropdownField
              icon={Calendar}
              value={dateLabel}
              placeholder={dateOffset === null}
              onPress={() => {
                setTimeOpen(false);
                setDateOpen((v) => !v);
              }}
            />
          </View>
          <View ref={timeRef} className="flex-1 gap-1">
            <Text className="text-xs text-light-text-primary dark:text-[#F5F6F8]">
              {activityStrings.customReminderSelect}
            </Text>
            <DropdownField
              icon={Clock}
              value={timeLabel}
              placeholder={minuteOfDay === null}
              onPress={() => {
                setDateOpen(false);
                setTimeOpen((v) => !v);
              }}
            />
          </View>
        </View>

        {/* Footer */}
        <View className="flex flex-row justify-end gap-sm pt-sm border-t border-light-border-secondary dark:border-[#222429]">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={activityStrings.customReminderCancel}
            onPress={close}
            className="flex flex-row items-center h-9 px-3 rounded-lg active:opacity-70"
          >
            <Text className="text-sm text-light-text-primary dark:text-[#F5F6F8]">
              {activityStrings.customReminderCancel}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={activityStrings.customReminderCreate}
            accessibilityState={{ disabled: !ready }}
            disabled={!ready}
            onPress={create}
            className={`flex flex-row items-center h-9 px-3 rounded-lg bg-[#537EFF] active:opacity-80 ${
              ready ? "" : "opacity-40"
            }`}
          >
            <Text className="text-sm font-bold text-white">
              {activityStrings.customReminderCreate}
            </Text>
          </Pressable>
        </View>
      </View>

      <AnchoredPopover
        anchorRef={dateRef}
        open={dateOpen}
        onClose={() => setDateOpen(false)}
        placement="bottom"
        align="start"
      >
        <DropdownList
          options={dateOptions.map((d, i) => ({
            key: String(i),
            label: d.label,
          }))}
          selectedKey={dateOffset !== null ? String(dateOffset) : null}
          onSelect={(k) => {
            setDateOffset(Number(k));
            setDateOpen(false);
          }}
        />
      </AnchoredPopover>
      <AnchoredPopover
        anchorRef={timeRef}
        open={timeOpen}
        onClose={() => setTimeOpen(false)}
        placement="bottom"
        align="start"
      >
        <DropdownList
          options={timeOptions.map((t) => ({
            key: String(t.minute),
            label: t.label,
          }))}
          selectedKey={minuteOfDay !== null ? String(minuteOfDay) : null}
          onSelect={(k) => {
            setMinuteOfDay(Number(k));
            setTimeOpen(false);
          }}
        />
      </AnchoredPopover>
    </Modal>
  );
}

/** Dropdown trigger field (Figma `Input Field`): 44 tall, r12, `#222429`
 *  border, leading glyph + value/placeholder + chevron. */
function DropdownField({
  icon: Icon,
  value,
  placeholder,
  onPress,
}: {
  icon: LucideIcon;
  value: string;
  placeholder: boolean;
  onPress: () => void;
}): JSX.Element {
  const pal = useIconPalette();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={value}
      onPress={onPress}
      className="flex flex-row items-center gap-sm h-11 px-md rounded-xl border border-light-border-secondary dark:border-[#222429] bg-light-surface-panel dark:bg-[#15171C] active:opacity-80"
    >
      <Icon size={18} color={pal.muted} strokeWidth={1.5} />
      <Text
        numberOfLines={1}
        className={`flex-1 text-sm ${
          placeholder
            ? "text-light-text-tertiary dark:text-[#B5BAC3]"
            : "text-light-text-primary dark:text-[#F5F6F8]"
        }`}
      >
        {value}
      </Text>
      <ChevronDown size={18} color={pal.icon} strokeWidth={1.5} />
    </Pressable>
  );
}

/** Scrollable option list shared by the two dropdowns + the sort popover look. */
function DropdownList({
  options,
  selectedKey,
  onSelect,
}: {
  options: ReadonlyArray<{ key: string; label: string }>;
  selectedKey: string | null;
  onSelect: (key: string) => void;
}): JSX.Element {
  return (
    <View className="min-w-[220px] max-h-[280px] rounded-2xl p-1 bg-white dark:bg-[#15171C] border border-light-border-secondary dark:border-[#2F333B]">
      <FlatList
        data={options}
        keyExtractor={(o) => o.key}
        renderItem={({ item }) => {
          const selected = item.key === selectedKey;
          return (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={item.label}
              accessibilityState={{ selected }}
              onPress={() => onSelect(item.key)}
              className="flex flex-row items-center justify-between gap-sm px-2 h-9 rounded-xl hover:bg-light-surface-secondary dark:hover:bg-[#222429] active:bg-light-surface-secondary dark:active:bg-[#222429]"
            >
              <Text
                className={`flex-1 text-sm font-bold ${
                  selected
                    ? "text-[#537EFF]"
                    : "text-light-text-primary dark:text-[#F5F6F8]"
                }`}
              >
                {item.label}
              </Text>
              {selected && <Check size={16} color={BRAND} strokeWidth={2} />}
            </Pressable>
          );
        }}
      />
    </View>
  );
}

// ── Sort popover (anchored to the "Sort by" button) ──────────────────────────

function ActivitySortPopover({
  anchorRef,
  open,
  active,
  onSelect,
  onClose,
}: {
  anchorRef: React.RefObject<View>;
  open: boolean;
  active: ActivitySort;
  onSelect: (sort: ActivitySort) => void;
  onClose: () => void;
}): JSX.Element | null {
  if (!open) return null;
  return (
    <AnchoredPopover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      placement="bottom"
      align="start"
    >
      <View className="min-w-[220px] rounded-2xl p-1 bg-white dark:bg-[#15171C] border border-light-border-secondary dark:border-[#2F333B]">
        {SORT_OPTIONS.map((sort) => {
          const selected = sort === active;
          return (
            <Pressable
              key={sort}
              accessibilityRole="button"
              accessibilityLabel={activityStrings.sort(sort)}
              accessibilityState={{ selected }}
              onPress={() => onSelect(sort)}
              className="flex flex-row items-center justify-between gap-sm px-2 h-9 rounded-xl hover:bg-light-surface-secondary dark:hover:bg-[#222429] active:bg-light-surface-secondary dark:active:bg-[#222429]"
            >
              <Text
                className={`flex-1 text-sm font-bold ${
                  selected
                    ? "text-[#537EFF]"
                    : "text-light-text-primary dark:text-[#F5F6F8]"
                }`}
              >
                {activityStrings.sort(sort)}
              </Text>
              {selected && <Check size={16} color={BRAND} strokeWidth={2} />}
            </Pressable>
          );
        })}
      </View>
    </AnchoredPopover>
  );
}

// ── Detail-pane placeholder (Figma `2804:108324`) ────────────────────────────

/** The empty right-hand pane of the desktop master-detail shell — shown until
 *  an activity is selected. `#111318` surface, centred `#B5BAC3` 16/400 text. */
export function ActivityDetailPlaceholder({
  className,
}: {
  className?: string;
}): JSX.Element {
  return (
    <View
      className={`flex-1 items-center justify-center bg-light-surface-base dark:bg-[#111318] border-l border-light-border-secondary dark:border-[#222429] ${
        className ?? ""
      }`}
    >
      <Text className="text-sm text-light-text-secondary dark:text-[#B5BAC3]">
        {activityStrings.detailPlaceholder}
      </Text>
    </View>
  );
}

// ── Empty + skeleton ─────────────────────────────────────────────────────────

function ActivityEmptyState(): JSX.Element {
  return (
    <View className="flex-1 items-center justify-center px-xl">
      <View className="items-center justify-center max-w-[320px]">
        <View className="mb-lg">
          <BellEmptyStateIllustration />
        </View>
        <Text className="text-lg leading-[29px] font-bold text-light-text-primary dark:text-[#F5F6F8] mb-xs text-center">
          {activityStrings.emptyTitle("all")}
        </Text>
        <Text className="text-sm text-[#6B7280] text-center leading-[22px]">
          {activityStrings.emptyBody}
        </Text>
      </View>
    </View>
  );
}

function ActivitySkeleton(): JSX.Element {
  return (
    <View accessibilityRole="progressbar" className="flex-1 px-md pt-sm">
      {Array.from({ length: 8 }).map((_, i) => (
        <View
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          key={i}
          className="flex flex-row items-start gap-md py-sm"
        >
          <View className="w-8 h-8 rounded-lg bg-light-surface-elevated dark:bg-white/[0.06] shrink-0 mt-xs" />
          <View className="flex-1 flex flex-col gap-[6px] pt-xs">
            <View className="h-[14px] rounded bg-light-surface-elevated dark:bg-white/[0.06] w-[40%]" />
            <View className="h-[12px] rounded bg-light-surface-elevated dark:bg-white/[0.06] w-[60%]" />
            <View className="h-[12px] rounded bg-light-surface-elevated dark:bg-white/[0.06] w-[80%]" />
          </View>
          <View className="h-[12px] w-8 rounded bg-light-surface-elevated dark:bg-white/[0.06] shrink-0 mt-xs" />
        </View>
      ))}
    </View>
  );
}

// ── Date grouping + time helpers ─────────────────────────────────────────────

type ActivitySectionRow =
  | { readonly type: "header"; readonly key: string; readonly label: string }
  | {
      readonly type: "row";
      readonly key: string;
      readonly item: ActivityItemRecord;
    };

const sectionRowKey = (r: ActivitySectionRow): string => r.key;

/** Flatten the (already-sorted) items into header/row entries grouped by the
 *  calendar day of each item's timestamp. */
function groupByDay(
  items: readonly ActivityItemRecord[],
  now: number = Date.now(),
): ActivitySectionRow[] {
  const out: ActivitySectionRow[] = [];
  let currentDay: string | null = null;
  for (const item of items) {
    const day = dayKey(item.timestamp);
    if (day !== currentDay) {
      currentDay = day;
      out.push({
        type: "header",
        key: `h:${day}`,
        label: dayLabel(item.timestamp, now),
      });
    }
    out.push({ type: "row", key: item.id, item });
  }
  return out;
}

function dayKey(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

function dayLabel(ts: number, now: number): string {
  const delta = startOfDay(now) - startOfDay(ts);
  if (delta <= 0) return "Today";
  if (delta === DAY_MS) return "Yesterday";
  const d = new Date(ts);
  const weekday = WEEKDAYS[d.getDay()] ?? "";
  if (delta < 7 * DAY_MS) return weekday;
  return `${weekday}, ${ordinal(d.getDate())} ${MONTHS[d.getMonth()] ?? ""}`;
}

const HOUR_MS = 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

export function formatRelative(
  timestamp: number,
  now: number = Date.now(),
): string {
  const delta = now - timestamp;
  if (delta < 0) return "now";
  if (delta < HOUR_MS) {
    const m = Math.max(1, Math.floor(delta / 60_000));
    return `${m}m`;
  }
  // Same calendar day → compact hours; otherwise a day label that mirrors the
  // section dividers (Figma trailing shows "Monday" / "Yesterday").
  const dayDelta = startOfDay(now) - startOfDay(timestamp);
  if (dayDelta <= 0) return `${Math.floor(delta / HOUR_MS)}h`;
  if (dayDelta === DAY_MS) return "Yesterday";
  if (dayDelta < WEEK_MS) return WEEKDAYS[new Date(timestamp).getDay()] ?? "";
  return new Date(timestamp).toISOString().slice(0, 10);
}

/** Tomorrow at 09:00 local, as a delta from now. */
function nextMorningDeltaMs(now: number = Date.now()): number {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.getTime() - now;
}

/** Next Monday at 09:00 local, as a delta from now (Figma "Next week"). */
function nextWeekDeltaMs(now: number = Date.now()): number {
  const d = new Date(now);
  const daysUntilMonday = (8 - d.getDay()) % 7 || 7; // always the FOLLOWING Monday
  d.setDate(d.getDate() + daysUntilMonday);
  d.setHours(9, 0, 0, 0);
  return d.getTime() - now;
}

/** Date dropdown options: Today + the next 13 days (Figma `Set date`). */
function buildDateOptions(
  now: number = Date.now(),
): ReadonlyArray<{ label: string; dayStart: number }> {
  const out: { label: string; dayStart: number }[] = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);
    const weekday = WEEKDAYS[d.getDay()] ?? "";
    const label =
      i === 0
        ? "Today"
        : i === 1
          ? "Tomorrow"
          : `${weekday}, ${ordinal(d.getDate())} ${MONTHS[d.getMonth()] ?? ""}`;
    out.push({ label, dayStart: d.getTime() });
  }
  return out;
}

/** Time dropdown options: every 30 minutes across the day (Figma `Set time`). */
function buildTimeOptions(): ReadonlyArray<{ minute: number; label: string }> {
  const out: { minute: number; label: string }[] = [];
  for (let m = 0; m < 24 * 60; m += 30) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    out.push({
      minute: m,
      label: `${h12}:${String(min).padStart(2, "0")} ${ampm}`,
    });
  }
  return out;
}
